import type { PipeIntegration, PipeIntegrationDef } from '../index'
import type { PipeEventProcessorDef, PipeInterface } from '@render/composables/usePipe'
import { ERROR_CONTEXTS } from '@render/composables/usePipe'
import { useFetch } from '@render/composables/useFetch'
import { isEuCountryCode } from './euCountries'

const GEO_DEVICE_KEY = 'var.global.geoDevice'
const IS_EU_COUNTRY_KEY = 'var.global.isEUCountry'
const USERS_IPS_KEY = 'var.global.usersIPs'
const GEO_DEVICE_FETCH_PROCESSOR = 'geo-device-fetch'
// Exported so pipeorders.ts can dispatch it via pipe.getEventProcessor(...)
// right after it writes var.global.originIssuerCountryCode — see
// applyOriginIssuerCountryCode there. originIssuerCountryCode is a direct
// input to determineIsEUCountry, but a same-IP order doesn't go through
// fetchGeoDevice (refreshGeoDeviceOnIpChange no-ops when the IP hasn't
// moved), so isEUCountry needs its own targeted recheck here.
export const GEO_DEVICE_CHECK_EU_COUNTRY_PROCESSOR = 'geo-device-check-eu-country'
// Exported so pipeorders.ts can dispatch it via pipe.getEventProcessor(...)
// right after it writes var.global.usersIPs — see applyUsersIps there.
export const GEO_DEVICE_REFRESH_ON_IP_CHANGE_PROCESSOR = 'geo-device-refresh-on-ip-change'

export interface GeoDeviceInfo {
  geo: {
    country: string | null
    region: string | null
    city: string | null
    lat: number | null
    lon: number | null
    timezone: string | null
    source: 'geoip-lite'
  }
  userAgent: {
    browser: string | null
    browserVersion: string | null
    os: string | null
    osVersion: string | null
    device: string
    isBot: boolean
  }
  ip: string | null
  referrer: string | null
  language: string | null
  createdAt: number
}

export interface GeoDeviceState {
  // First-ever fetch in this persisted session — never overwritten again.
  initial: GeoDeviceInfo
  // Most recent fetch — overwritten every time the processor fires (e.g. a
  // returning abandoned-cart visitor whose IP/device may have changed).
  current: GeoDeviceInfo
}

// True if ANY of the three signals indicates an EU country:
//  1. var.global.originIssuerCountryCode — BIN-lookup issuing country of
//     the card actually used (only present once a payment exists).
//  2. var.global.geoDevice.current.geo.country — most recent IP-geo lookup.
//  3. var.global.geoDevice.initial.geo.country — first-ever IP-geo lookup
//     this session, in case a later fetch failed and cleared `current`.
// All three are already ISO 3166-1 alpha-2 codes (geoip-lite's `country`
// field and the orders API's BIN lookup use the same format), so no
// code-to-name conversion is needed — see euCountries.ts.
function determineIsEUCountry(pipe: PipeInterface): boolean {
  const originIssuerCountryCode = pipe.getData('var.global.originIssuerCountryCode') as
    | string
    | null
    | undefined
  if (isEuCountryCode(originIssuerCountryCode)) return true

  const geoDevice = pipe.getData(GEO_DEVICE_KEY) as GeoDeviceState | undefined
  if (isEuCountryCode(geoDevice?.current?.geo?.country)) return true
  if (isEuCountryCode(geoDevice?.initial?.geo?.country)) return true

  return false
}

const geoDeviceIntegration: PipeIntegration = (): PipeIntegrationDef => {
  const { get } = useFetch()
  // getEventProcessors() only receives config at registration time — per
  // convention (see pipeorders.ts), config needed at handler-execution time
  // is persisted here during setup() rather than relied on from that arg.
  let apiHost: string

  async function fetchGeoDevice(pipe: PipeInterface) {
    if (typeof window === 'undefined') return

    await pipe.whenReady()

    try {
      const response = await get<GeoDeviceInfo>(`${apiHost}/api/public/geo`)
      const existing = pipe.getData(GEO_DEVICE_KEY) as GeoDeviceState | undefined
      pipe.setData(GEO_DEVICE_KEY, {
        initial: existing?.initial ?? response,
        current: response,
      } satisfies GeoDeviceState)
      await checkEuCountry(pipe)
    } catch (e) {
      pipe.trackError({ error: e, context: ERROR_CONTEXTS.geoDeviceFetch })
    }
  }

  // Standalone (not auto-chained off fetchGeoDevice) — originIssuerCountryCode
  // only exists post-purchase, so authors may want the flag (re-)computed at a
  // point later than the geo fetch, e.g. on the post-purchase page's onPageShow.
  async function checkEuCountry(pipe: PipeInterface) {
    if (typeof window === 'undefined') return

    await pipe.whenReady()
    pipe.setData(IS_EU_COUNTRY_KEY, determineIsEUCountry(pipe))
  }

  // Cache-invalidation guard for fetchGeoDevice. var.global.usersIPs (set by
  // pipeorders on every order upsert / AC restore) reflects the IP the
  // backend most recently saw for this visitor; var.global.geoDevice.current
  // is a point-in-time IP-geo/UA lookup taken whenever the fetch processor
  // last ran. If the two diverge (VPN toggle, mobile network handoff,
  // abandoned-cart link resumed from a different network), the geoDevice
  // snapshot is stale — re-fetch. Intended to be wired to a funnel-wide event
  // (e.g. onPageShow on every page) so geoDevice self-heals without a fetch
  // on every single navigation. No-op when usersIPs hasn't been set yet
  // (nothing newer to compare against) or when the IPs already match.
  async function refreshGeoDeviceOnIpChange(pipe: PipeInterface) {
    if (typeof window === 'undefined') return

    await pipe.whenReady()

    const usersIPs = pipe.getData(USERS_IPS_KEY) as
      | { initial?: string | null; current?: string | null }
      | undefined
    const currentUserIp = usersIPs?.current
    if (!currentUserIp) return

    const geoDevice = pipe.getData(GEO_DEVICE_KEY) as GeoDeviceState | undefined
    if (geoDevice?.current?.ip === currentUserIp) return

    await fetchGeoDevice(pipe)
  }

  return {
    setup(pipe: PipeInterface, config: { apiHost: string }) {
      apiHost = config.apiHost

      // Seed a stable default so visibility rules and interpolation never see
      // `undefined` before the check processor has run. Runs on SSR too (no
      // `typeof window` guard here) — mirrors isAbandonedCartViewed in
      // pipeorders.ts.
      if (pipe.getData(IS_EU_COUNTRY_KEY) === undefined) {
        pipe.setData(IS_EU_COUNTRY_KEY, false)
      }
    },

    // Authors attach these processors to whichever event they want (a CTA's
    // onClick, a page's onPageShow, etc.) via "Add processor" — no automatic
    // fetch/check on pipe init. Mirrors pipeorders' pipeorders-create-order /
    // pipeorders-navigate-to-app processors.
    getEventProcessors(): PipeEventProcessorDef[] {
      if (typeof window === 'undefined') return []
      return [
        {
          type: GEO_DEVICE_FETCH_PROCESSOR,
          handler: ({ pipe }) => fetchGeoDevice(pipe),
        },
        {
          type: GEO_DEVICE_CHECK_EU_COUNTRY_PROCESSOR,
          handler: ({ pipe }) => checkEuCountry(pipe),
        },
        {
          type: GEO_DEVICE_REFRESH_ON_IP_CHANGE_PROCESSOR,
          handler: ({ pipe }) => refreshGeoDeviceOnIpChange(pipe),
        },
      ]
    },
  }
}

export default geoDeviceIntegration
